import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contentAPI } from '@/utils/api';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CheckCircle } from 'lucide-react';
import { Loader } from '@/components/ui/loader';

gsap.registerPlugin(ScrollTrigger);

export const WorkingModel = () => {
  const { t, i18n } = useTranslation();
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSteps();
  }, []);

  const fetchSteps = async () => {
    try {
      const res = await contentAPI.getWorkingModel();
      setSteps(res.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching working model:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading && steps.length > 0) {
      gsap.from('.step-card', {
        scrollTrigger: {
          trigger: '.timeline-container',
          start: 'top 70%'
        },
        opacity: 0,
        x: -50,
        duration: 0.8,
        stagger: 0.2
      });
    }
  }, [loading, steps]);

  const getLangContent = (content) => {
    return content?.[i18n.language] || content?.en || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary">
        <Loader className="text-secondary" text={t('common.loading')} />
      </div>
    );
  }

  return (
    <div className="bg-primary min-h-screen">
      {/* Hero Section */}
      <section
        data-testid="working-model-hero"
        className="relative py-32 flex items-center justify-center"
        style={{
          backgroundImage: `linear-gradient(rgba(11, 28, 45, 0.85), rgba(11, 28, 45, 0.85)), url('https://images.unsplash.com/photo-1758691736493-aa6d22c0f8a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBib2FyZHJvb218ZW58MHx8fHwxNzcyMjY5ODc4fDA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-4">
            Our Working Model
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            A structured, proven approach to corporate debt recovery and resolution
          </p>
        </div>
      </section>

      {/* Timeline Section */}
      <section data-testid="timeline-section" className="py-24 md:py-32">
        <div className="timeline-container container mx-auto px-4 md:px-8 max-w-5xl">
          <div className="space-y-12">
            {steps.map((step, index) => (
              <div
                key={step.step_number}
                data-testid={`step-${step.step_number}`}
                className="step-card relative"
              >
                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className="absolute left-6 top-20 w-0.5 h-full bg-secondary/30 hidden md:block" />
                )}

                <div className="flex flex-col md:flex-row gap-6">
                  {/* Step Number */}
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center relative z-10">
                      <span className="text-primary font-bold text-xl font-heading">{step.step_number}</span>
                    </div>
                  </div>

                  {/* Step Content */}
                  <div className="flex-grow glass-card p-8 rounded-sm">
                    <h3 className="font-heading text-2xl font-semibold text-white mb-3">
                      {getLangContent(step.title)}
                    </h3>
                    <p className="text-gray-300 text-base leading-relaxed">
                      {getLangContent(step.description)}
                    </p>
                    <div className="mt-4 flex items-center text-secondary text-sm">
                      <CheckCircle size={16} className="mr-2" />
                      Step {step.step_number} of {steps.length}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
